<template>
  <Bubble
    :chart-data="chartData"
    :chart-options="chartOptions"
    :styles="wrapperStyles"
  />
</template>

<script>
import 'chart.js/auto'
import { Bubble } from 'vue-chartjs/legacy'
import { Chart } from 'chart.js'
import ChartjsPluginDatalabels from 'chartjs-plugin-datalabels'

Chart.register(ChartjsPluginDatalabels)

const COLOR_UP = '#56CA00'
const COLOR_DOWN = '#FF4C51'
const COLOR_FLAT = '#FFB400'

function defaultColor(x) {
  if (x > 0) return COLOR_UP
  if (x < 0) return COLOR_DOWN

  return COLOR_FLAT
}

const zoneBackgroundPlugin = {
  id: 'zoneBackground',
  beforeDatasetsDraw(chart, _args, opts) {
    const zones = opts && opts.zones
    if (!zones || !zones.length) return
    const { ctx, chartArea, scales } = chart
    const { left, right } = chartArea
    zones.forEach(z => {
      const top = scales.y.getPixelForValue(z.to)
      const bottom = scales.y.getPixelForValue(z.from)
      if (bottom <= chartArea.top || top >= chartArea.bottom) return
      const yTop = Math.max(top, chartArea.top)
      const yBottom = Math.min(bottom, chartArea.bottom)
      ctx.fillStyle = z.color
      ctx.fillRect(left, yTop, right - left, yBottom - yTop)

      if (z.label) {
        ctx.fillStyle = z.labelColor || 'rgba(0,0,0,0.55)'
        ctx.font = 'bold 13px Tahoma, sans-serif'
        ctx.textBaseline = 'top'
        ctx.fillText(z.label, left + 8, yTop + 10)
      }
    })
  },
}

const innerArrowPlugin = {
  id: 'innerArrow',
  afterDatasetDraw(chart, args) {
    const { ctx } = chart
    const { meta } = args
    const { data } = chart.data.datasets[0]

    ctx.save()
    data.forEach((point, i) => {
      const { trending } = point
      if (!trending) return

      const { x, y } = meta.data[i]
      const r = point.r || 6

      ctx.fillStyle = '#FFFFFF'
      ctx.beginPath()

      if (trending > 0) {
        const tipY = y - r + 3
        const baseY = tipY + 6
        ctx.moveTo(x, tipY)
        ctx.lineTo(x - 3, baseY)
        ctx.lineTo(x + 3, baseY)
      } else {
        const tipY = y + r - 3
        const baseY = tipY - 6
        ctx.moveTo(x, tipY)
        ctx.lineTo(x - 3, baseY)
        ctx.lineTo(x + 3, baseY)
      }
      ctx.closePath()
      ctx.fill()
    })
    ctx.restore()
  },
}

Chart.register(zoneBackgroundPlugin, innerArrowPlugin)

export default {
  name: 'HeatmapChart',

  components: { Bubble },

  props: {
    bubbles: {
      type: Array,
      default: () => [],
    },
    xRange: {
      type: Array,
      default: () => [-16, 16],
    },
    yRange: {
      type: Array,
      default: () => [0, 5],
    },
    xStep: {
      type: Number,
      default: 2,
    },
    yStep: {
      type: Number,
      default: 1,
    },
    xUnit: {
      type: String,
      default: '%',
    },
    xLabel: {
      type: String,
      default: 'X',
    },
    yLabel: {
      type: String,
      default: 'Y',
    },
    zones: {
      type: Array,
      default: () => [],
    },
    xHighlights: {
      type: Array,
      default: () => [],
    },
    yHighlights: {
      type: Array,
      default: () => [],
    },
    colorFn: {
      type: Function,
      default: null,
    },
    radius: {
      type: Number,
      default: 6,
    },
  },

  data() {
    return {
      wrapperStyles: {
        position: 'relative',
        height: '100%',
        width: '100%',
      },
    }
  },

  computed: {
    chartData() {
      const colorOf = b => (this.colorFn ? this.colorFn(b) : defaultColor(b.x))
      const data = this.bubbles.map(b => ({
        x: b.x,
        y: b.y,
        r: b.r != null ? b.r : this.radius,
        symbol: b.symbol,
        trending: b.trending,
      }))

      return {
        datasets: [{
          data,
          backgroundColor: this.bubbles.map(b => `${colorOf(b)}99`),
          borderColor: this.bubbles.map(b => colorOf(b)),
          borderWidth: 1.5,
        }],
      }
    },

    chartOptions() {
      const colorOf = b => (this.colorFn ? this.colorFn(b) : defaultColor(b.x))

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            type: 'linear',
            min: this.xRange[0],
            max: this.xRange[1],
            grid: {
              color: ctx => (this.xHighlights.includes(ctx.tick.value) ? 'rgba(125,125,125,0.5)' : 'rgba(125,125,125,0.08)'),
              lineWidth: ctx => (this.xHighlights.includes(ctx.tick.value) ? 1.5 : 1),
            },
            ticks: {
              stepSize: this.xStep,
              callback: v => `${v}${this.xUnit}`,
              color: 'rgba(125,125,125,0.7)',
              font: { size: 10 },
            },
          },
          y: {
            type: 'linear',
            min: this.yRange[0],
            max: this.yRange[1],
            grid: {
              color: ctx => (this.yHighlights.includes(ctx.tick.value) ? 'rgba(125,125,125,0.5)' : 'rgba(125,125,125,0.08)'),
              lineWidth: ctx => (this.yHighlights.includes(ctx.tick.value) ? 1.5 : 1),
            },
            ticks: {
              stepSize: this.yStep,
              color: 'rgba(125,125,125,0.7)',
              font: { size: 10 },
            },
          },
        },
        plugins: {
          legend: { display: false },
          zoneBackground: { zones: this.zones },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: ctx => ctx[0].raw.symbol,
              label: ctx => {
                const { x, y } = ctx.raw
                const sign = x >= 0 ? '+' : ''

                return [
                  `${this.yLabel}: ${y.toFixed(2)}`,
                  `${this.xLabel}: ${sign}${x.toFixed(2)}${this.xUnit}`,
                ]
              },
            },
          },
          datalabels: {
            anchor: 'center',
            align: 'right',
            offset: 6,
            color: ctx => colorOf(ctx.dataset.data[ctx.dataIndex]),
            font: { weight: 'bold', size: 10 },
            formatter: v => v.symbol,
          },
        },
      }
    },
  },
}
</script>
